import { XR_BUTTON_HOLD_SECONDS, XR_NOTICE_SECONDS } from './_data/spaceCanvas.js';
import { applyDeadZone } from './_pure/spaceCanvasHelpers.js';
/** Raw xr-standard gamepad indices as reported by Quest Touch controllers; they do not depend on a downloaded profile. */
export const XR_BUTTONS = {
    a: { hand: 'right', index: 4, name: 'A' },
    b: { hand: 'right', index: 5, name: 'B' },
    'right-stick': { hand: 'right', index: 3, name: 'Stick press' },
    x: { hand: 'left', index: 4, name: 'X' },
    y: { hand: 'left', index: 5, name: 'Y' },
    'left-stick': { hand: 'left', index: 3, name: 'Stick press' },
};
const BUTTON_ORDER = Object.keys(XR_BUTTONS);
/**
 * Single-owner controller shortcuts. Owners bind named buttons; one frame driver feeds controller input.
 * Content-agnostic: owners decide what a gesture means and request feedback through notices and indicators.
 */
export function createXrBindingRegistry() {
    const bindings = new Map();
    const presses = new Map();
    const physical = new Map();
    const indicators = new Map();
    const stickCaptures = new Map();
    const listeners = new Set();
    let haptics = [];
    let legendVisible = false;
    let notice;
    let noticeAge = 0;
    let serial = 0;
    let snapshot;
    const publish = () => {
        snapshot = {
            legendVisible, ...(notice ? { notice } : {}),
            indicators: Object.fromEntries([...indicators].map(([hand, entry]) => [hand, entry.indicator])),
            legend: BUTTON_ORDER.flatMap(button => {
                const binding = bindings.get(button);
                if (!binding)
                    return [];
                return [{ hand: XR_BUTTONS[button].hand, button: XR_BUTTONS[button].name, lines: [binding.legend,
                            ...(binding.hold ? [`hold: ${binding.hold.legend}`] : []),
                            ...(binding.triggerLegend ? [`+ trigger: ${binding.triggerLegend}`] : [])] }];
            }),
        };
        listeners.forEach(listener => listener());
    };
    publish();
    const pulse = (hand, intensity, milliseconds) => { haptics.push({ hand, intensity, milliseconds }); };
    const cancel = (button) => {
        if (!presses.delete(button))
            return;
        bindings.get(button)?.onCancel?.();
    };
    const notify = (text) => {
        notice = { text, serial: ++serial };
        noticeAge = 0;
        publish();
    };
    return {
        bind(button, binding) {
            const existing = bindings.get(button);
            if (existing)
                throw new Error(`XR ${XR_BUTTONS[button].hand} ${XR_BUTTONS[button].name} is already bound by `
                    + `${existing.owner}; ${binding.owner} cannot claim it.`);
            bindings.set(button, binding);
            publish();
            return () => {
                if (bindings.get(button) !== binding)
                    return;
                cancel(button);
                bindings.delete(button);
                publish();
            };
        },
        update(input, delta) {
            if (notice && (noticeAge += delta) >= XR_NOTICE_SECONDS) {
                notice = undefined;
                publish();
            }
            for (const button of BUTTON_ORDER) {
                const { hand, index } = XR_BUTTONS[button];
                const source = input[hand];
                const down = source?.buttons[index] === true;
                const rising = down && !physical.get(button);
                physical.set(button, down);
                const binding = bindings.get(button);
                const press = presses.get(button);
                if (!binding)
                    continue;
                if (rising && !press) {
                    presses.set(button, { held: 0, holding: false, trigger: source.trigger });
                    pulse(hand, 0.25, 25);
                    binding.onPress?.();
                    if (!binding.hold)
                        binding.onTap?.();
                }
                else if (press && down) {
                    press.held += delta;
                    if (binding.hold && !press.holding && press.held >= XR_BUTTON_HOLD_SECONDS) {
                        press.holding = true;
                        pulse(hand, 0.4, 35);
                        binding.hold.onStart?.();
                    }
                    if (press.holding)
                        binding.hold?.onStickX?.(applyDeadZone(source.stickX), delta);
                    if (source.trigger && !press.trigger)
                        binding.onTrigger?.();
                    press.trigger = source.trigger;
                }
                else if (press && !down) {
                    presses.delete(button);
                    if (binding.hold) {
                        if (press.holding)
                            binding.hold.onEnd?.();
                        else
                            binding.onTap?.();
                    }
                    binding.onRelease?.(press.held);
                }
            }
        },
        /** Ends every press without release semantics, e.g. when the XR session ends. */
        reset() {
            [...presses.keys()].forEach(cancel);
            physical.clear();
            haptics = [];
            if (notice) {
                notice = undefined;
                publish();
            }
        },
        captureStick(hand, owner, active) {
            const existing = stickCaptures.get(hand);
            if (existing)
                throw new Error(`XR ${hand} stick capture is owned by ${existing.owner}; ${owner} cannot claim it.`);
            const capture = { owner, active };
            stickCaptures.set(hand, capture);
            return () => { if (stickCaptures.get(hand) === capture)
                stickCaptures.delete(hand); };
        },
        capturesStick: (hand) => !!stickCaptures.get(hand)?.active() || [...presses.keys()].some(button => XR_BUTTONS[button].hand === hand && !!bindings.get(button)?.hold?.onStickX),
        capturesTrigger: (hand) => [...presses.keys()].some(button => XR_BUTTONS[button].hand === hand && !!bindings.get(button)?.onTrigger),
        setIndicator(hand, owner, indicator) {
            const existing = indicators.get(hand);
            if (existing && existing.owner !== owner) {
                if (!indicator)
                    return;
                throw new Error(`XR ${hand} indicator is owned by ${existing.owner}; ${owner} cannot claim it.`);
            }
            if (indicator)
                indicators.set(hand, { owner, indicator });
            else
                indicators.delete(hand);
            publish();
        },
        setLegendVisible(visible) {
            if (visible === legendVisible)
                return;
            legendVisible = visible;
            publish();
        },
        notify,
        /** A refused shortcut: a notice plus a strong, long pulse distinct from the light press pulse. */
        reject(hand, text) {
            pulse(hand, 0.9, 120);
            notify(text);
        },
        takeHaptics() {
            const pending = haptics;
            haptics = [];
            return pending;
        },
        getSnapshot: () => snapshot,
        subscribe(listener) {
            listeners.add(listener);
            return () => { listeners.delete(listener); };
        },
    };
}
export const spaceCanvasXrBindings = createXrBindingRegistry();
